import PropTypes from 'prop-types'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'

import agent from 'agent'
import { isKubernetes } from 'components/attacks/create/targets/target-utils'
import KubernetesTargets from 'components/attacks/list/KubernetesTargets'
import Button from 'components/common/Button'
import DataTable from 'components/common/DataTable'
import DayPicker from 'components/common/DayPicker'
import { EmptyState, EmptyStateTitle } from 'components/common/EmptyState'
import { EmptyStateMessage } from 'components/common/EmptyStateMessage'
import Flex from 'components/common/Flex'
import Pill from 'components/common/Pill'
import Popover from 'components/common/Popover'
import PopoverMenu from 'components/common/PopoverMenu'
import Text from 'components/common/Text'
import TextButton from 'components/common/TextButton'
import Icon from 'components/Icon'
import MaxRuns from 'components/schedules/MaxRuns'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import useScenarioScheduleDetails from 'hooks/use-scenario-schedule-details'
import toast from 'lib/toast'
import theme from 'theme'
import { messageFromResponseError, pluralize } from 'utils/strings'

import TimePeriodForInvocation from './TimePeriodForInvocation'

function ScheduleListItem({ schedule, currentTeamId, doFetch }) {
  const {
    isLoading,
    hasError,
    scenarioDetails = {}
  } = useScenarioScheduleDetails(schedule.scenario_id)

  if (isLoading || hasError) {
    return null
  }

  const {
    name,
    stepCount,
    firstTargetParam,
    targetDefinition,
    additionalTargetParamsAmount
  } = scenarioDetails

  const isFirstAttackKubernetes = isKubernetes(targetDefinition?.targetType)

  return (
    <Fragment>
      <div>
        <Text as="h5">{name}</Text>
        <div>
          <MaxRuns
            amount={schedule.trigger.maxRuns}
            css={{ display: 'inline' }}
          />
          <Text as="small" css={{ display: 'inline' }}>
            , {stepCount} {pluralize('step', stepCount)}
          </Text>
        </div>
      </div>
      <TimePeriodForInvocation schedule={schedule} />
      <DayPicker isStatic activeDays={schedule.trigger.activeDays} />
      <Flex>
        {isFirstAttackKubernetes ? (
          <KubernetesTargets targets={targetDefinition?.strategy?.k8sObjects} />
        ) : null}
        {!isFirstAttackKubernetes && firstTargetParam ? (
          <Pill
            key="firstParam"
            tooltip={`${firstTargetParam[0]}: ${firstTargetParam[1]}`}
          >
            {firstTargetParam[1]}
          </Pill>
        ) : null}
        {!isFirstAttackKubernetes && additionalTargetParamsAmount ? (
          <Pill key="paramCount" type="gray">
            + {additionalTargetParamsAmount}
          </Pill>
        ) : null}
      </Flex>
      <div>
        <Popover alignPopover={Popover.ALIGN_RIGHT}>
          {({ getTargetProps, getPopoverProps }) => (
            <Fragment>
              <Icon
                data-test-schedule-actions={schedule.guid}
                icon="solidEllipsisVertical"
                {...getTargetProps({
                  css: {
                    fontSize: theme.fontSizes[3],
                    color: theme.colors.steel[0],

                    '&:hover': {
                      color: theme.colors.steel[2]
                    }
                  }
                })}
              />
              <PopoverMenu getPopoverProps={getPopoverProps}>
                <TextButton
                  destructive
                  data-test-schedule-delete={schedule.guid}
                  onClick={async (event) => {
                    event.preventDefault()
                    try {
                      await agent.delete(
                        `/schedules/scenarios/${schedule.guid}?teamId=${currentTeamId}`
                      )
                      toast({
                        type: 'success',
                        text: 'Schedule deleted.'
                      })
                    } catch (err) {
                      toast({
                        type: 'error',
                        text: messageFromResponseError(
                          err,
                          'Unable to cancel schedule. Please try again later.'
                        )
                      })
                    }
                    doFetch()
                  }}
                >
                  Delete Scenario Schedule
                </TextButton>
              </PopoverMenu>
            </Fragment>
          )}
        </Popover>
      </div>
    </Fragment>
  )
}

ScheduleListItem.propTypes = {
  schedule: PropTypes.object,
  currentTeamId: PropTypes.string,
  doFetch: PropTypes.func
}

export default function ScenariosScheduleList() {
  const { currentTeamId } = useAuth()

  const { data, isError, isLoading, refetch } = useGremlinQuery(
    '/schedules/scenarios'
  )

  return (
    <DataTable
      columnTitles={['Name', 'Time', 'Days', 'Targets', false]}
      data={data}
      error={isError}
      getRowLinkTo={({ scenario_id }) => `/scenarios/detail/${scenario_id}`}
      gridColumns="minmax(48px, 1fr) minmax(48px, 128px) minmax(256px, 1fr) minmax(128px, 1fr) 32px"
      loading={isLoading}
      renderEmpty={() => (
        <EmptyStateMessage
          data-test-scenarios-list-empty
          icon="calendar"
          title="No Schedules Available"
        >
          <Link to="/scenario/new">Create a scenario</Link> to schedule.
        </EmptyStateMessage>
      )}
      renderError={(message) => (
        <EmptyState>
          <EmptyStateTitle>
            {message || 'There was an error loading schedules'}
          </EmptyStateTitle>
          <Button
            loading={isLoading}
            type="button"
            onClick={() => {
              refetch()
            }}
          >
            Retry
          </Button>
        </EmptyState>
      )}
      renderRow={(schedule) => (
        <ScheduleListItem
          currentTeamId={currentTeamId}
          doFetch={refetch}
          schedule={schedule}
        />
      )}
    />
  )
}
